import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { RAG_BASE_URL } from '@/config/rag';
import { getAuthToken } from '@/features/auth/session';
import {
  createCollection,
  deleteCollection,
  listCollections,
  RagApiError,
} from '@/services/ragApi';

import { truncateMiddle } from '../lib/documentName';
import type { CreateCollectionRequest } from '../types';

export const collectionKeys = {
  all: ['collections'] as const,
};

export function useCollections() {
  const token = getAuthToken();
  return useQuery({
    queryKey: collectionKeys.all,
    queryFn: () => listCollections(RAG_BASE_URL, token ?? ''),
    enabled: Boolean(token),
  });
}

export function useCreateCollection() {
  const qc = useQueryClient();
  const token = getAuthToken();
  return useMutation({
    mutationFn: (body: CreateCollectionRequest) =>
      createCollection(RAG_BASE_URL, token ?? '', body),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: collectionKeys.all });
      // Id truncado: o campo aceita até 128 caracteres, e o toast não comporta isso.
      toast.success(`Coleção "${truncateMiddle(vars.collection_id, 32)}" criada.`);
    },
    onError: (err) => {
      const msg = err instanceof RagApiError ? err.message : 'Erro ao criar coleção.';
      toast.error(msg);
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  const token = getAuthToken();
  return useMutation({
    mutationFn: (collectionId: string) => deleteCollection(RAG_BASE_URL, token ?? '', collectionId),
    onSuccess: (_, collectionId) => {
      void qc.invalidateQueries({ queryKey: collectionKeys.all });
      toast.success(`Coleção "${truncateMiddle(collectionId, 32)}" removida.`);
    },
    onError: (err) => {
      const msg = err instanceof RagApiError ? err.message : 'Erro ao remover coleção.';
      toast.error(msg);
    },
  });
}
